/**
 * CreateAgentPage — "Create a new agent" entry point.
 *
 * "Simple" is a single-screen wizard: textarea, suggested ideas, checklist.
 * It reuses AddAgentWizard's underlying code — the same customAgentAPI.wizardChat
 * contract and system prompt, called in the same conversational loop, so a
 * clarifying question from the AI is answered by typing again in this same
 * textarea rather than switching UIs.
 *
 * "Advanced" renders the same body/right-pane building blocks as the full
 * Agent Builder workspace (AgentWorkspaceCenter + RuntimeConfigPanel) inline
 * on this same page/URL, instead of navigating to /agent-builder/new — that
 * route still exists unchanged for editing existing agents and for cloning
 * a community template (CommunityPage.tsx), this page just doesn't jump to
 * it anymore for a brand-new agent.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Check, Search, FileText, Mail, CalendarClock, BarChart3, PenLine, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { customAgentAPI, mcpAPI, agentRunAPI, skillsAPI } from '../services/api.js';
import { useAI } from '../context/AIContext.jsx';
import { AGENT_WIZARD_SYSTEM_PROMPT } from '../components/agentsWorkflows/AddAgentWizard.jsx';
import AgentWorkspaceCenter from '../components/agentWorkspace/AgentWorkspaceCenter.tsx';
import RuntimeConfigPanel from '../components/agentWorkspace/RuntimeConfigPanel.tsx';
import { SKILL_LIBRARY } from '../components/agentWorkspace/workspaceData.ts';

// Matches the app-wide palette (Agents.jsx, AgentDetailsPage.tsx, etc.) —
// this page previously carried the older pre-revamp warm-beige tokens
// (var(--lyra-color-surface-2)/var(--lyra-color-border)/var(--lyra-color-text)), which read as visibly off against every other
// page's now-standard neutral grays.
const T = {
  page: 'var(--lyra-color-surface)',
  surface: 'var(--lyra-color-surface)',
  border: 'var(--lyra-color-border)',
  ink: 'var(--lyra-color-text)',
  ink2: 'var(--app-text-2)',
  ink3: 'var(--lyra-color-text-muted)',
  ink4: 'var(--app-text-3)',
  accent: 'var(--lyra-color-primary)',
  accentDark: 'var(--app-primary-deep)',
  accentLight: 'var(--app-primary-soft)',
  green: 'var(--lyra-color-success)',
};

const DEFAULT_SUGGESTED = [
  { label: 'Research competitors', icon: Search },
  { label: 'Create reports', icon: FileText },
  { label: 'Monitor inbox', icon: Mail },
  { label: 'Schedule meetings', icon: CalendarClock },
  { label: 'Analyze data', icon: BarChart3 },
  { label: 'Write content', icon: PenLine },
];

const CHECKLIST = [
  'Writes the instructions',
  'Selects the right tools',
  'Configures the settings',
  'You can always customize later',
];

type WizardTurn = { role: 'user' | 'assistant'; content: string };

export default function CreateAgentPage() {
  const navigate = useNavigate();
  const { models, selectedModelId, selectedMode } = useAI();

  // ── Simple mode (wizard chat) ─────────────────────────────────────────────
  const [mode, setMode] = useState<'simple' | 'advanced'>('simple');
  const [value, setValue] = useState('');
  const [creating, setCreating] = useState(false);
  const [history, setHistory] = useState<WizardTurn[]>([]);
  const [followUp, setFollowUp] = useState<string | null>(null);
  const [dynamicChips, setDynamicChips] = useState<string[]>([]);

  const handleCreate = async () => {
    const text = value.trim();
    if (!text || creating) return;
    setCreating(true);
    const newHistory: WizardTurn[] = [...history, { role: 'user', content: text }];
    setHistory(newHistory);
    setValue('');
    try {
      const data: any = await customAgentAPI.wizardChat(newHistory, AGENT_WIZARD_SYSTEM_PROMPT, undefined, undefined);
      const raw = typeof data.result === 'string' ? data.result : JSON.stringify(data.result);
      let parsed: any = null;
      try { parsed = JSON.parse(raw.replace(/```json|```/g, '').trim()); } catch { parsed = null; }

      if (!parsed) {
        toast.error("Didn't quite catch that — try describing it differently.");
        return;
      }
      setHistory((h) => [...h, { role: 'assistant', content: raw }]);

      const draft = parsed.agentDraft;
      if (draft?.name && draft?.systemPrompt) {
        const payload = {
          ...draft,
          examples: (draft.examples || []).filter((e: string) => e && e.trim()),
          defaultModel: draft.defaultModel || 'gemini',
        };
        const { agent } = await customAgentAPI.create(payload);
        toast.success(`"${agent.name}" created!`);
        navigate(`/agents/${agent._id || agent.id}`);
      } else {
        // Not enough detail yet — stay on this same screen and ask for the
        // rest, continuing the same conversation on the next "Create agent" click.
        setFollowUp(parsed.message || 'Tell me a bit more about what this agent should do.');
        setDynamicChips(Array.isArray(parsed.suggestions) ? parsed.suggestions : []);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Could not create the agent. Try again.');
    } finally {
      setCreating(false);
    }
  };

  const chips = dynamicChips.length > 0
    ? dynamicChips.map((label) => ({ label, icon: Sparkles }))
    : DEFAULT_SUGGESTED;

  // ── Advanced mode (Agent Builder body + right pane, inline) ───────────────
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [domain, setDomain] = useState('general');
  const [defaultModel, setDefaultModel] = useState('');
  const [personality, setPersonality] = useState('');
  // No icon picker in this embedded view (that lived in AgentBuilder's own
  // top bar, which isn't reused here) — stays at the schema default.
  const [icon] = useState('🤖');
  const [enabled, setEnabled] = useState(true);
  const [goal, setGoal] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [memoryEnabled, setMemoryEnabled] = useState(false);
  const [contextNote, setContextNote] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(2048);
  const [schedule, setSchedule] = useState('');
  const [inputHint, setInputHint] = useState('');
  const [requiredInputType, setRequiredInputType] = useState('');
  const [mcpServers, setMcpServers] = useState<any[]>([]);
  const [selectedMcpIds, setSelectedMcpIds] = useState<string[]>([]);
  const [mcpEnabled, setMcpEnabled] = useState(false);
  const [inputSchema, setInputSchema] = useState('');
  const [cadence, setCadence] = useState<'one_shot' | 'recurring'>('one_shot');
  const [recurrenceRule, setRecurrenceRule] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [notifyOnCompletion, setNotifyOnCompletion] = useState(false);
  const [notificationChannels, setNotificationChannels] = useState<string[]>([]);
  const [serverNameConflict, setServerNameConflict] = useState<string | null>(null);
  const [advSaving, setAdvSaving] = useState(false);
  const [advSaved, setAdvSaved] = useState(false);

  useEffect(() => {
    mcpAPI.listServers()
      .then((r: any) => setMcpServers((r.servers || []).filter((s: any) => s.enabled)))
      .catch(() => {});
  }, []);

  const switchToAdvanced = () => {
    // Continuity: don't discard whatever was typed in the Simple textarea —
    // seed it into Description once, if Description hasn't been touched yet.
    if (value.trim() && !description.trim()) setDescription(value.trim());
    setMode('advanced');
  };

  const toggleTool = async (toolId: string) => {
    const adding = !(selectedTools || []).includes(toolId);
    setSelectedTools(prev => prev.includes(toolId) ? prev.filter(id => id !== toolId) : [...prev, toolId]);
    try {
      if (toolId === 'lyra:gmail-reader') {
        if (adding) await skillsAPI.install('gmail');
        else await skillsAPI.uninstall('gmail');
      }
      if (toolId === 'lyra:calendar-assistant') {
        if (adding) await skillsAPI.install('google-calendar');
        else await skillsAPI.uninstall('google-calendar');
      }
    } catch (e) {
      console.error('Failed to install/uninstall registry skill for', toolId, e);
      setSelectedTools(prev => prev.includes(toolId) ? prev.filter(id => id !== toolId) : [...prev, toolId]);
    }
  };

  const removeSkill = (toolId: string) =>
    setSelectedTools(prev => prev.filter(id => id !== toolId));

  const handleAdvancedSave = useCallback(async () => {
    if (!name.trim()) { toast.error('Agent name is required'); return; }
    if (notifyOnCompletion && notificationChannels.length === 0) {
      toast.error('Select at least one notify channel before saving'); return;
    }
    if (cadence === 'recurring' && !recurrenceRule) {
      toast.error('Pick a recurrence frequency before saving'); return;
    }
    setAdvSaving(true);
    const payload: any = {
      name: name.trim(), description: description.trim(), category: domain, defaultModel,
      personality: personality.trim(), icon, enabled, goal: goal.trim(),
      systemPrompt: systemPrompt.trim(), skills: selectedTools.map(id => {
        const wsSkill = SKILL_LIBRARY.find(s => s.id === id);
        const promptText = wsSkill?.prompt;
        return promptText ? { skillId: id, promptText } : id;
      }), memoryEnabled,
      context: contextNote.trim(), temperature, maxTokens,
      schedule: schedule.trim(), examples: [],
      mcpServerIds: selectedMcpIds,
      inputHint: inputHint.trim(),
      requiredInputType: requiredInputType || null,
      mcpEnabled,
      inputSchema: (() => { try { return inputSchema.trim() ? JSON.parse(inputSchema) : null; } catch { return null; } })(),
      cadence,
      recurrenceRule: cadence === 'recurring' ? recurrenceRule : null,
      timezone: cadence === 'recurring' ? timezone : null,
      notifyOnCompletion,
      notificationChannels,
    };
    try {
      const res: any = await customAgentAPI.create(payload);
      toast.success('Agent created!');
      const savedAgent = res?.agent || null;
      const savedAgentId = savedAgent?._id || savedAgent?.id;
      if (schedule && schedule.trim() && savedAgentId) {
        try {
          const created = await agentRunAPI.scheduleRun({ agentId: savedAgentId, agentName: name.trim(), scheduledAt: new Date(schedule).toISOString() });
          try { window.dispatchEvent(new CustomEvent('lyra:agent-scheduled', { detail: created.run || created })); } catch (e) {}
        } catch (e) {
          console.warn('Failed to create scheduled run:', e);
        }
      }
      setAdvSaved(true);
      setTimeout(() => navigate('/agents'), 800);
    } catch (e: any) {
      const serverMsg = e?.response?.data?.error || e?.message || '';
      if (e?.response?.status === 409 || /lyran name taken|duplicate lyran/i.test(serverMsg)) {
        const msg = e?.response?.data?.error || 'Duplicate Lyran! not allowed..';
        setServerNameConflict(msg);
        toast.error(msg);
      } else {
        toast.error('Failed to save agent');
      }
    } finally { setAdvSaving(false); }
  }, [
    name, description, domain, defaultModel, personality, icon, enabled, goal,
    systemPrompt, selectedTools, memoryEnabled, contextNote, temperature,
    maxTokens, schedule, selectedMcpIds, inputHint, requiredInputType,
    mcpEnabled, inputSchema, cadence, recurrenceRule, timezone,
    notifyOnCompletion, notificationChannels, navigate,
  ]);

  const isAdvanced = mode === 'advanced';

  return (
    <div style={{ minHeight: '100%', background: T.page, padding: '28px 32px 60px' }}>
      {/* Same max-width in both modes — Advanced needs the extra room, and
          having Simple narrower caused a jarring width jump every time you
          switched tabs. */}
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        <button
          onClick={() => navigate('/agents')}
          style={{ display: 'flex', alignItems: 'center', gap: 5, border: 'none', background: 'none', color: T.ink3, fontSize: 12, cursor: 'pointer', marginBottom: 14, padding: 0 }}
        >
          <ArrowLeft size={13} /> All agents
        </button>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 600, color: T.ink, marginBottom: 4 }}>Create a new agent</div>
            <div style={{ fontSize: 13, color: T.ink3 }}>
              {isAdvanced ? 'Full manual control over prompt, tools, model, schedule and every other setting.' : 'Describe what you want this agent to do. Lyra will set it up for you.'}
            </div>
          </div>
          <div style={{ display: 'flex', border: `1px solid ${T.border}`, borderRadius: 10, padding: 3, background: T.surface }}>
            <button
              onClick={() => setMode('simple')}
              style={{
                border: 'none',
                borderRadius: 8,
                padding: '7px 16px',
                fontSize: 12.5,
                fontWeight: 600,
                cursor: isAdvanced ? 'pointer' : 'default',
                background: isAdvanced ? 'transparent' : T.accentDark,
                color: isAdvanced ? T.ink3 : 'var(--lyra-color-surface)',
              }}
            >
              Simple
            </button>
            <button
              onClick={switchToAdvanced}
              title="Full manual control over prompt, tools, model, schedule and every other setting"
              style={{
                border: 'none',
                borderRadius: 8,
                padding: '7px 16px',
                fontSize: 12.5,
                fontWeight: 600,
                cursor: isAdvanced ? 'default' : 'pointer',
                background: isAdvanced ? T.accentDark : 'transparent',
                color: isAdvanced ? 'var(--lyra-color-surface)' : T.ink3,
              }}
            >
              Advanced
            </button>
          </div>
        </div>

        {isAdvanced ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 20, alignItems: 'start' }}>
            <AgentWorkspaceCenter
              name={name}                       setName={setName}
              description={description}         setDescription={setDescription}
              domain={domain}                   setDomain={setDomain}
              personality={personality}         setPersonality={setPersonality}
              goal={goal}                       setGoal={setGoal}
              inputHint={inputHint}             setInputHint={setInputHint}
              requiredInputType={requiredInputType} setRequiredInputType={setRequiredInputType}
              systemPrompt={systemPrompt}       setSystemPrompt={setSystemPrompt}
              activeSkillIds={selectedTools}
              onToggleSkill={toggleTool}
              onRemoveSkill={removeSkill}
              externalNameConflict={serverNameConflict || undefined}
              onNameEdit={() => { if (serverNameConflict) setServerNameConflict(null); }}
              hideHeader
            />
            {/* Sticky + height-bounded: RuntimeConfigPanel is built as a
                fixed-height panel with its Save button pinned as a sticky
                footer (designed for AgentBuilder's 100vh layout) — without a
                bounded height here its `height:100%` has nothing to resolve
                against, so it just grows to match the (much taller) center
                column and the Save button ends up wherever that happens to
                end. This restores its intended fixed-height, own-scroll,
                always-reachable-footer behavior inside a normally-scrolling
                page. */}
            <div style={{ position: 'sticky', top: 20, height: 'calc(100vh - 40px)' }}>
              <RuntimeConfigPanel
                models={models as any[] || []}
                selectedModelId={selectedModelId || undefined}
                selectedMode={selectedMode || 'auto'}
                defaultModel={defaultModel}       setDefaultModel={setDefaultModel}
                temperature={temperature}         setTemperature={setTemperature}
                maxTokens={maxTokens}             setMaxTokens={setMaxTokens}
                memoryEnabled={memoryEnabled}     setMemoryEnabled={setMemoryEnabled}
                contextNote={contextNote}         setContextNote={setContextNote}
                mcpServers={mcpServers}
                selectedMcpIds={selectedMcpIds}   setSelectedMcpIds={setSelectedMcpIds}
                schedule={schedule}               setSchedule={setSchedule}
                enabled={enabled}                 setEnabled={setEnabled}
                mcpEnabled={mcpEnabled}           setMcpEnabled={setMcpEnabled}
                inputSchema={inputSchema}         setInputSchema={setInputSchema}
                cadence={cadence}                 setCadence={setCadence}
                recurrenceRule={recurrenceRule}   setRecurrenceRule={setRecurrenceRule}
                timezone={timezone}               setTimezone={setTimezone}
                notifyOnCompletion={notifyOnCompletion}     setNotifyOnCompletion={setNotifyOnCompletion}
                notificationChannels={notificationChannels} setNotificationChannels={setNotificationChannels}
                saving={advSaving}
                saved={advSaved}
                onSave={handleAdvancedSave}
              />
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 260px', gap: 20, alignItems: 'start' }}>
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 10 }}>
                {followUp ? followUp : '1. Tell us what this agent should do'}
              </div>
              <textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                maxLength={500}
                rows={4}
                placeholder={followUp ? 'Type your answer…' : 'e.g. Research competitors every Monday and send me a summary\nCreate LinkedIn posts, Monitor my inbox, Analyze sales data...'}
                style={{
                  width: '100%', border: `1px solid ${T.border}`, borderRadius: 10, padding: '12px 14px',
                  fontSize: 13.5, color: T.ink, resize: 'vertical', outline: 'none', fontFamily: 'inherit',
                }}
              />
              <div style={{ textAlign: 'right', fontSize: 10.5, color: T.ink4, marginTop: 4 }}>{value.length}/500</div>

              <div style={{ fontSize: 11.5, fontWeight: 600, color: T.ink3, margin: '14px 0 8px' }}>Suggested ideas</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 }}>
                {chips.map((s) => {
                  const Icon = s.icon;
                  return (
                    <button
                      key={s.label}
                      onClick={() => setValue((v) => (v ? `${v}\n${s.label}` : s.label))}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        border: `1px solid ${T.border}`, background: 'var(--lyra-color-surface)', color: T.ink2,
                        borderRadius: 999, padding: '6px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                      }}
                    >
                      <Icon size={12} /> {s.label}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={handleCreate}
                disabled={!value.trim() || creating}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  width: '100%', border: 'none', borderRadius: 10, padding: '12px 0',
                  background: T.accentDark, color: 'var(--app-on-accent)', fontSize: 13.5, fontWeight: 600,
                  cursor: !value.trim() || creating ? 'not-allowed' : 'pointer',
                  opacity: !value.trim() || creating ? 0.6 : 1,
                }}
              >
                {creating ? 'Setting up your agent…' : 'Create agent'}
              </button>
            </div>

            <div style={{ background: T.accentLight, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20, textAlign: 'center' }}>
              <div style={{
                width: 46, height: 46, borderRadius: 12, background: T.accentDark, color: 'var(--app-on-accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px',
              }}>
                <Sparkles size={20} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, marginBottom: 12 }}>Let Lyra handle the setup</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9, textAlign: 'left' }}>
                {CHECKLIST.map((c) => (
                  <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Check size={14} color={T.green} style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: T.ink2 }}>{c}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
